/*
 * The 30 days of weather before a field collection, from Open-Meteo, asked
 * by the collector's own phone after the record is made (Field Mode's
 * outbox; docs/FIELD_AI_VISION_SPEC.md Part I section 4). The API Lambda has
 * no internet access, which is why the phone asks (routes/weather.py).
 *
 * What leaves the phone: the position ROUNDED TO TWO DECIMALS (about a
 * kilometre; the user's decision of 2026-10-21) and two dates. No account,
 * no record id, no token: a plain request, no credentials, no referrer.
 *
 * The window is the 30 days ending on the collection day. A recent window
 * comes from the forecast service, which keeps about three months behind it;
 * an older one from the historical archive, which runs a few days behind
 * the present.
 *
 * Pure apart from weatherRequest's URL and fetchWeatherWindow's fetch, so
 * the mapping is tested on a recorded answer (openMeteo.test.js).
 */

export const WINDOW_DAYS = 30;
/** A window ending this recently comes from the forecast service. */
export const RECENT_DAYS = 60;
export const FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';
export const ARCHIVE_URL = 'https://archive-api.open-meteo.com/v1/archive';
/** A day with this much rain or more counts as a rain day. */
export const RAIN_DAY_MM = 1;

const DAY_MS = 86400000;

/** Rounded to two decimals: about a kilometre, as decided. */
export const roundCoord = (value) => Math.round(Number(value) * 100) / 100;

/** A local calendar date as YYYY-MM-DD. */
export function isoDay(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The request for the window ending on `collectedAt`, or null without a place. */
export function weatherRequest(location, collectedAt, now = new Date()) {
  const lat = Number(location?.lat);
  const lng = Number(location?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !(collectedAt instanceof Date)
      || Number.isNaN(collectedAt.getTime())) return null;
  const end = new Date(Math.min(collectedAt.getTime(), now.getTime()));
  // By calendar days, so a change of clocks inside the window moves no date.
  const start = new Date(end);
  start.setDate(start.getDate() - (WINDOW_DAYS - 1));
  const recent = now.getTime() - end.getTime() <= RECENT_DAYS * DAY_MS;
  const params = new URLSearchParams({
    latitude: roundCoord(lat).toFixed(2),
    longitude: roundCoord(lng).toFixed(2),
    start_date: isoDay(start),
    end_date: isoDay(end),
    daily: 'temperature_2m_max,temperature_2m_min,precipitation_sum',
    hourly: 'temperature_2m,relative_humidity_2m',
    timezone: 'auto',
  });
  return `${recent ? FORECAST_URL : ARCHIVE_URL}?${params}`;
}

const finite = (values) => (values || []).filter((v) => typeof v === 'number' && Number.isFinite(v));
const round2 = (v) => Math.round(v * 100) / 100;
const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);

/** Kilometres between two points (the grid cell the answer is for, and the point asked). */
export function distanceKm(a, b) {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Open-Meteo's answer as PUT /collections/{id}/weather takes it, or null when
 * it holds no usable day. `asked` is the rounded point that was sent.
 */
export function weatherBody(answer, asked) {
  const daily = answer?.daily;
  if (!daily || !Array.isArray(daily.time) || !daily.time.length) return null;
  const highs = finite(daily.temperature_2m_max);
  const lows = finite(daily.temperature_2m_min);
  const rain = finite(daily.precipitation_sum);
  const temps = finite(answer.hourly?.temperature_2m);
  const humidity = finite(answer.hourly?.relative_humidity_2m);
  if (!highs.length && !lows.length && !rain.length) return null;
  const body = {
    station_name: 'Open-Meteo',
    window_days: WINDOW_DAYS,
    temp_min_c: lows.length ? round2(Math.min(...lows)) : null,
    temp_max_c: highs.length ? round2(Math.max(...highs)) : null,
    temp_mean_c: temps.length ? round2(mean(temps)) : null,
    rainfall_total_mm: rain.length ? round2(rain.reduce((a, b) => a + b, 0)) : null,
    rainfall_days: rain.length ? rain.filter((mm) => mm >= RAIN_DAY_MM).length : null,
    humidity_mean_pct: humidity.length ? round2(Math.min(100, Math.max(0, mean(humidity)))) : null,
    daily: daily.time.map((date, i) => ({
      date,
      temp_min_c: daily.temperature_2m_min?.[i] ?? null,
      temp_max_c: daily.temperature_2m_max?.[i] ?? null,
      rainfall_mm: daily.precipitation_sum?.[i] ?? null,
    })),
  };
  // The model's grid cell is not the point asked: how far, as a station's.
  if (asked && Number.isFinite(answer.latitude) && Number.isFinite(answer.longitude)) {
    body.station_distance_km = round2(distanceKm(asked, { lat: answer.latitude, lng: answer.longitude }));
  }
  return body;
}

/**
 * Ask Open-Meteo for the window. Answers { body } (null when it had nothing),
 * or throws when it could not be asked or answered with an error.
 */
export async function fetchWeatherWindow(location, collectedAt, { now = new Date(), fetcher } = {}) {
  const url = weatherRequest(location, collectedAt, now);
  if (!url) return { body: null };
  const get = fetcher || ((u, o) => globalThis.fetch(u, o));
  const response = await get(url, { credentials: 'omit', referrerPolicy: 'no-referrer' });
  if (!response?.ok) {
    const error = new Error(`Open-Meteo answered ${response?.status}`);
    error.status = response?.status;
    throw error;
  }
  const asked = { lat: roundCoord(location.lat), lng: roundCoord(location.lng) };
  return { body: weatherBody(await response.json(), asked) };
}
